import { Card, Typography, Input, theme, Flex } from 'antd';
import React, { useState, useRef, useEffect } from 'react';
import '../../styles/EditableCard.css';
import { timeAgo } from 'cms/src/utils/common';
import ReactTimeago from 'react-timeago';

const { useToken } = theme;
const { Title, Text } = Typography;

const EditableCard = ({ metaData, isEditable, updateFilename, onEnterPress }) => {
  const { token } = useToken();
  const [fileName, setFileName] = useState(metaData?.fileName);
  const inputRef = useRef(null); // Reference for the input element
  const timeoutRef = useRef(null);

  // Sync the fileName state with metaData.fileName when it changes
  useEffect(() => {
    if (metaData?.fileName) {
      setFileName(metaData.fileName);
    }
  }, [metaData?.fileName]);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (fileName) {
        updateFilename(fileName);
      }
    }, 1300);

    return () => clearTimeout(timeoutRef.current);
  }, [fileName, updateFilename]);

  const saveCaretPosition = () => {
    const input = inputRef.current;
    const caretPosition = input.selectionStart;
    return caretPosition;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (fileName) updateFilename(fileName);
      onEnterPress();
    }
  };

  // Function to restore caret position
  const restoreCaretPosition = (caretPosition) => {
    const input = inputRef.current;
    input.selectionStart = caretPosition;
    input.selectionEnd = caretPosition;
  };

  // Handle changes to the title while maintaining caret position
  const handleFileNameChange = (e) => {
    const newFileName = e.target.value;
    const caretPosition = saveCaretPosition();
    setFileName(newFileName);
    requestAnimationFrame(() => {
      restoreCaretPosition(caretPosition);
    });
  };

  return (
    <Card
      className="file-title-card"
      style={{ border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }}
      styles={{
        body: {
          padding: 0,
          paddingLeft: token.paddingSM,
          paddingRight: token.paddingSM,
          marginBottom: token.marginLG,
        },
      }}
    >
      <div className="file-title-container">
        {isEditable ? (
          <Input
            ref={inputRef}
            value={fileName}
            onKeyDown={handleKeyDown}
            onChange={handleFileNameChange}
            placeholder="New Page title"
            className="editable-title-input"
          />
        ) : (
          <Title className="file-heading">{fileName}</Title>
        )}
      </div>
      <Flex>
        <Text type="secondary" style={{ fontWeight: 500 }}>
          {`Created `}{' '}
          <ReactTimeago
            live={false}
            date={new Date(metaData?.createdAt)}
            formatter={(value, unit) => {
              if (unit === 'second') return `just now`;
              return `${value} ${unit} ago`;
            }}
          />{' '}
          {` by ${metaData?.author}`} • Last modified{' '}
          {metaData?.author !== metaData?.updatedBy && (
            <>
              <ReactTimeago
                live={false}
                date={new Date(metaData?.lastUpdate)}
                formatter={(value, unit) => {
                  if (unit === 'second') return 'just now';
                  return `${value} ${unit} ago`;
                }}
              />
              {` by ${metaData?.lastUpdatedBy}`}
            </>
          )}
        </Text>
      </Flex>
    </Card>
  );
};

export default EditableCard;
